import React from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import {
  Button,
  ButtonColorVariant,
  Body2,
  Box,
  AlignItems,
  Color,
  Display,
  Heading1,
  Icon,
  IconVariant,
  JustifyContent,
  Bold,
  Space,
  Text,
  FlexDirection,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { browserHistory } from 'react-router'

import routes from 'config/routes'
import logger from 'utils/logger'

import { OrderOneOffBoxBanner } from './OrderOneOffBoxBanner'
import OrdersList from './OrdersList'

class MyDeliveries extends React.PureComponent {
  componentDidMount() {
    this.fetchOrdersAndAddresses()
  }

  getErrorMessage() {
    const { didErrorFetchingPendingOrders, didErrorFetchingProjectedOrders } = this.props

    const errorMessages = {
      pendingOrderError:
        "We're not able to display your upcoming orders right now. Please try again later.",
      projectedOrderError:
        "We're not able to display your future deliveries right now. Please try again later.",
      genericError: "We're not able to display your deliveries right now. Please try again later.",
    }

    if (didErrorFetchingProjectedOrders !== null) {
      return errorMessages.projectedOrderError
    } else if (didErrorFetchingPendingOrders !== null) {
      return errorMessages.pendingOrderError
    } else {
      return errorMessages.genericError
    }
  }

  fetchOrdersAndAddresses = async () => {
    const { userId, userLoadData, userLoadNewOrders } = this.props

    try {
      if (!userId) {
        await userLoadData()
      }

      userLoadNewOrders(true)
    } catch (error) {
      logger.error(error)
    }
  }

  retryFetch = () => {
    this.fetchOrdersAndAddresses()
  }

  static renderFetchError = (retryFetch, errorMessage) => (
    <Box
      display={Display.Flex}
      flexDirection={[
        FlexDirection.Column,
        FlexDirection.Row,
        FlexDirection.Row,
        FlexDirection.Row,
      ]}
      bg={Color.Error_50}
      borderColor={Color.Error_100}
      width="100%"
      paddingV={3}
      paddingH={3}
      borderRadius={1.5}
      data-testid="info-panel"
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.SpaceBetween}
    >
      <Box
        display={Display.Flex}
        paddingBottom={[5, 'auto', 'auto']}
        paddingV={[5, 'auto', 'auto']}
        alignItems={AlignItems.Center}
      >
        <Icon name="warning" size={5} variant={IconVariant.Warning} />
        <Space direction="horizontal" size={2} />
        <Text size={1}>
          <Bold>{errorMessage}</Bold>
        </Text>
      </Box>
      <Button
        colorVariant={ButtonColorVariant.Primary}
        onClick={retryFetch}
        style={{ minHeight: 48 }}
        width={['100%', 'auto', 'auto', 'auto']}
      >
        Retry
      </Button>
    </Box>
  )

  handleOrderAnotherBoxClick = () => {
    datadogRum.addAction('order_another_box_my_deliveries_button_clicked')
    browserHistory.push(routes.client.deliveryDetails)
  }

  static renderLoading = (
    <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
      <Loading />
    </Box>
  )

  renderOrders() {
    const {
      didErrorFetchingPendingOrders,
      didErrorFetchingProjectedOrders,
      isFetchingOrders,
      userId,
    } = this.props
    if (isFetchingOrders || !userId) {
      return MyDeliveries.renderLoading
    }

    if (didErrorFetchingPendingOrders !== null || didErrorFetchingProjectedOrders !== null) {
      const errorMessage = this.getErrorMessage()
      const error = didErrorFetchingPendingOrders

      datadogLogs.logger.error('MyDeliveries: orders fetching failed', {
        userId,
        error,
        renderedErrorMessage: errorMessage,
      })

      return MyDeliveries.renderFetchError(this.retryFetch, errorMessage)
    }

    return <OrdersList />
  }

  render() {
    return (
      <Box
        data-testid="myDeliveries"
        maxWidth={'1170px'}
        paddingV={4}
        paddingH={4}
        style={{ margin: '0 auto' }}
      >
        <Helmet title="Gousto Deliveries | Manage All Upcoming Deliveries" />
        <Box paddingBottom={8} paddingV={8} paddingH={3} paddingRight={3}>
          <Box display={Display.Flex} alignItems={AlignItems.Baseline}>
            <Heading1 size={[9, 11]}>Deliveries</Heading1>
            <Box
              flexGrow={1}
              style={{
                borderBottom: '1px solid var(--color-Mackerel)',
                marginBottom: '0.5rem',
                marginLeft: '0.5rem',
              }}
            />
          </Box>
          <Space direction="horizontal" size={4} />

          <Box
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            paddingBottom={8}
            style={{ flexWrap: 'wrap' }}
          >
            <Body2
              size={4}
              style={{
                flex: '2 1',
                marginBottom: '0.75rem',
                marginRight: '0.5rem',
                minWidth: '300px',
              }}
            >
              Here you can manage all of your upcoming deliveries, select recipes, skip or cancel
              orders as you need.
            </Body2>
          </Box>
          <OrderOneOffBoxBanner onOrderClick={this.handleOrderAnotherBoxClick} />
        </Box>
        {this.renderOrders()}
      </Box>
    )
  }
}

MyDeliveries.propTypes = {
  isFetchingOrders: PropTypes.bool,
  didErrorFetchingPendingOrders: PropTypes.shape({ message: PropTypes.string }),
  didErrorFetchingProjectedOrders: PropTypes.shape({ message: PropTypes.string }),
  userLoadData: PropTypes.func.isRequired,
  userLoadNewOrders: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
}

MyDeliveries.defaultProps = {
  isFetchingOrders: false,
  didErrorFetchingPendingOrders: null,
  didErrorFetchingProjectedOrders: null,
}

export default MyDeliveries
